'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import clsx from 'clsx'
import { useController, useFormContext } from 'react-hook-form'

import type {
  Question,
  QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import {
  PrescriptionQuizFooter,
  QuizFieldError,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'

type ConsultationQuestions = Exclude<
  QuestionnairePage,
  'upload-supporting-document' | 'personal-information' | 'call-arrangement'
>

const QUESTION_LABELS: Record<string, string> = {
  description: 'What would you like to talk to the doctor about?',
  isForSelf: 'Is this consultation for yourself?',
  isOlderThan18: 'Are you 18 or older?',
  hasSignificantMedicalConditions:
    'Do you have any significant current or past medical conditions?',
  significantMedicalConditions: 'Tell us about those medical conditions.',
  isTakingRegularMedications: 'Are you taking any regular medications?',
  regularMedications: 'Tell us which regular medications you take.',
  hasAllergies: 'Do you have any allergies — medicines, food, anything else?',
  allergies: 'Tell us about those allergies.',
  allowsAutoNoteTakingSoftware:
    'Can your doctor use secure note-taking software during the consultation?',
  isInAustralia: 'Are you in Australia right now?',
  tncAccepted: 'Do you accept the terms and conditions?',
}

export function ConsultationQuestionnaireFields({
  currentStep,
  firstPage,
  busy,
  onBack,
}: {
  currentStep: ConsultationQuestions
  firstPage: boolean
  busy: boolean
  onBack: () => void
}) {
  return (
    <>
      <motion.div
        animate="animate"
        className="flex flex-col gap-7 sm:gap-8"
        initial="initial"
        variants={prescriptionQuestionListMotion}
      >
        {currentStep.questions.map((question) => (
          <motion.div
            key={questionKey(question)}
            variants={prescriptionQuestionItemMotion}
          >
            <ConsultationQuestionField question={question} />
          </motion.div>
        ))}
      </motion.div>

      <PrescriptionQuizFooter
        backHidden={firstPage}
        nextLoading={busy}
        submit
        onBack={onBack}
      />
    </>
  )
}

function ConsultationQuestionField({ question }: { question: Question }) {
  switch (question.type) {
    case 'free-text':
      return (
        <QuizTextArea
          hint={
            question.key === 'description'
              ? 'A few sentences is plenty — the doctor will ask follow-ups on the call.'
              : undefined
          }
          label={QUESTION_LABELS[question.key] || question.title}
          name={question.key}
          placeholder={
            question.key === 'description'
              ? "For example: I've had a dry cough for two weeks and it isn't settling."
              : 'Add details for the doctor'
          }
          rows={question.key === 'description' ? 6 : 4}
        />
      )
    case 'yes-no':
      return (
        <QuizChoiceQuestion
          label={QUESTION_LABELS[question.key] || question.title}
          name={question.key}
          options={[
            { id: 'Yes', label: 'Yes' },
            { id: 'No', label: 'No' },
          ]}
          termsLink={question.key === 'tncAccepted'}
        />
      )
    default:
      return null
  }
}

function QuizTextArea({
  name,
  label,
  placeholder,
  hint,
  rows,
}: {
  name: string
  label: string
  placeholder: string
  hint?: string
  rows: number
}) {
  const { field, fieldState } = useController({ name })
  const showError = fieldState.isTouched && Boolean(fieldState.error)

  return (
    <fieldset>
      <label
        className="mb-3 block text-[15px] font-semibold leading-[21px] sm:text-[16.5px] sm:leading-[23px]"
        htmlFor={name}
      >
        {label}
      </label>
      <textarea
        {...field}
        className={clsx(
          'min-h-[120px] w-full resize-y rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 py-3.5 text-[15px] leading-[22px] text-[#0B0B0F] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F55] focus:shadow-[0_0_0_3px_rgba(11,11,15,0.05)] sm:min-h-[150px] sm:px-[18px]',
          showError && 'border-[#C6453C] focus:border-[#C6453C]',
        )}
        id={name}
        placeholder={placeholder}
        rows={rows}
        value={field.value ?? ''}
      />
      {hint && (
        <p className="mt-2.5 text-[12.5px] leading-[18px] text-[#82828B] sm:text-[13px]">
          {hint}
        </p>
      )}
      {showError && (
        <QuizFieldError>{fieldState.error?.message}</QuizFieldError>
      )}
    </fieldset>
  )
}

function QuizChoiceQuestion({
  name,
  label,
  options,
  termsLink = false,
}: {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
  termsLink?: boolean
}) {
  const { field, fieldState } = useController({ name })
  const { clearErrors, setValue } = useFormContext()
  const showError =
    (fieldState.isTouched || Boolean(field.value)) && Boolean(fieldState.error)

  return (
    <fieldset>
      <legend className="mb-3 text-[15px] font-semibold leading-[21px] sm:text-[16.5px] sm:leading-[23px]">
        {label}
      </legend>
      {termsLink && (
        <Link
          className="-mt-1 mb-3 inline-flex text-[13px] font-semibold leading-4 text-[#2563EB] hover:underline"
          href="/page/terms-of-service"
          target="_blank"
        >
          Read the terms →
        </Link>
      )}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
        {options.map((option) => {
          const selected = String(field.value) === String(option.id)
          const selectedError = selected && showError

          return (
            <label
              className={clsx(
                'flex min-h-[55px] cursor-pointer items-center rounded-[16px] border-[1.5px] bg-[#F4F4F6] px-4 text-[15px] font-normal leading-5 transition-[border-color,color,background-color] duration-300 sm:px-5',
                selectedError
                  ? 'border-[#C6453C] text-[#0B0B0F] hover:bg-[#EFEFF2]'
                  : selected
                    ? 'border-[#0B0B0F] text-[#0B0B0F] hover:bg-[#EFEFF2]'
                    : 'border-transparent text-[#5C5C64] hover:bg-[#EFEFF2]',
              )}
              key={option.id}
            >
              <input
                checked={selected}
                className="sr-only"
                name={field.name}
                onBlur={field.onBlur}
                onChange={() => {
                  // The previous answer's error must not flash while this one validates.
                  clearErrors(name)
                  setValue(name, option.id, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  })
                }}
                type="radio"
                value={option.id}
              />
              <span>{option.label}</span>
              <span
                aria-hidden="true"
                className={clsx(
                  'ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-[border-color,background-color] duration-300',
                  selectedError
                    ? 'border-[#C6453C] bg-[#C6453C]'
                    : selected
                      ? 'border-[#0B0B0F] bg-[#0B0B0F]'
                      : 'border-[#C4C4CA] bg-white',
                )}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </label>
          )
        })}
      </div>
      {showError && (
        <QuizFieldError>{fieldState.error?.message}</QuizFieldError>
      )}
    </fieldset>
  )
}

function questionKey(question: Question) {
  switch (question.type) {
    case 'duration':
      return `${question.yearKey}-${question.monthKey}`
    case 'date-range':
      return `${question.startKey}-${question.endKey}`
    default:
      return question.key
  }
}
